/**
 * captureEntryWithResult.ts
 * -----------------------------------------------------------------
 * src/modules/timeline/services/capture/captureEntryWithResult.ts
 *
 * ver1 - 29 October 2026 (Offline capture sync fix - Layer 2):
 *   - New capture path that reports WHY a save failed instead of
 *     returning a bare null. The offline queue stores this reason
 *     and the Letterbox can show a calm failed state.
 *   - Sends client_capture_id (unique per capture, made on the device).
 *     The database refuses a second row with the same ID for the same
 *     person (migration lukaduka-migration-offline-capture-sync-ver1,
 *     index idx_entries_user_client_capture). A duplicate means the
 *     capture already arrived on an earlier attempt, so it is reported
 *     as SUCCESS (alreadyExisted: true), never as a failure.
 *   - entry_date can be locked at capture time (entryDate), so a
 *     capture made in another timezone keeps its real local date even
 *     if it syncs days later somewhere else.
 *   - server_received_at is NEVER sent. The server stamps it
 *     (trigger entries_stamp_server_received_at).
 *   - Never logs entry content. Only error codes and messages.
 *
 * Plan: lukaduka-offline-sync-fix-plan-29102026-ver2.md
 *
 * Note: captureEntry() in timelineService.ts still exists and is
 * unchanged. Once the queue (Layer 3) and QuickCaptureSheet both use
 * this function, captureEntry becomes a thin wrapper around it so
 * there is only ONE capture path.
 * -----------------------------------------------------------------
 */

import { supabase } from '@/shared/services/supabase';
import { todayLocalISO, toLocalISODate } from '@/shared/utils/localDate';
import type { TimelineEntry, CaptureInput } from '@/modules/timeline/types/timeline.types';

// Name of the unique index created in the Layer 1 migration.
// Used to tell "this capture already arrived" apart from any other
// duplicate-key error.
const CLIENT_CAPTURE_UNIQUE_INDEX = 'idx_entries_user_client_capture';

// Postgres error code for a unique-constraint violation.
const PG_UNIQUE_VIOLATION = '23505';

export interface CaptureEntryParams {
  /** Owner of the entry. Must be the signed-in user (RLS enforces this). */
  userId: string;
  /** Headline + optional captured GPS + device capture time. */
  input: CaptureInput;
  /** Unique ID for this capture, generated on the device (crypto.randomUUID()). */
  clientCaptureId: string;
  /** Save straight to Safespace instead of the Letterbox. */
  toSafespace?: boolean;
  /**
   * Local calendar date (YYYY-MM-DD) recorded on the device AT CAPTURE.
   * Preferred over everything else. Locks the date where the moment
   * actually happened.
   */
  entryDate?: string;
  /**
   * Fallback for older queued captures that have no entryDate stored:
   * the ISO time of capture. Converted using the device's CURRENT
   * timezone, which can be a day out after travel - that is why
   * entryDate is preferred.
   */
  capturedAtIso?: string;
}

export type CaptureEntryResult =
  | {
      ok: true;
      /** The saved row. Can be null only if it already existed and could not be re-read. */
      entry: TimelineEntry | null;
      /** True when an earlier attempt had already saved this capture. */
      alreadyExisted: boolean;
    }
  | {
      ok: false;
      /** Postgres / PostgREST error code, or 'NETWORK' / 'UNKNOWN'. */
      errorCode: string;
      /** Human-readable reason, for storing on the queue item and support. */
      errorMessage: string;
    };

function resolveEntryDate(params: CaptureEntryParams): string {
  if (params.entryDate && /^\d{4}-\d{2}-\d{2}$/.test(params.entryDate)) {
    return params.entryDate;
  }
  if (params.capturedAtIso) {
    return toLocalISODate(params.capturedAtIso);
  }
  return todayLocalISO();
}

function isClientCaptureDuplicate(code: string | undefined, message: string | undefined): boolean {
  return code === PG_UNIQUE_VIOLATION && !!message && message.includes(CLIENT_CAPTURE_UNIQUE_INDEX);
}

// Re-read a capture that already arrived on an earlier attempt.
// Best-effort: if this read fails, the capture is still safely saved.
async function fetchExistingCapture(
  userId: string,
  clientCaptureId: string
): Promise<TimelineEntry | null> {
  const { data, error } = await supabase
    .from('entries')
    .select('*')
    .eq('user_id', userId)
    .eq('client_capture_id', clientCaptureId)
    .maybeSingle();

  if (error) {
    console.error('[captureEntryWithResult] existing capture re-read error:', error.code, error.message);
    return null;
  }
  return (data as TimelineEntry | null) ?? null;
}

export async function captureEntryWithResult(
  params: CaptureEntryParams
): Promise<CaptureEntryResult> {
  const { userId, input, clientCaptureId, toSafespace = false } = params;

  try {
    const { data, error } = await supabase
      .from('entries')
      .insert({
        user_id: userId,
        title: input.title,
        entry_date: resolveEntryDate(params),
        status: toSafespace ? 'safespace' : 'letterbox',
        entry_type: 'standard',
        captured_lat: input.captured_lat ?? null,
        captured_lng: input.captured_lng ?? null,
        captured_address: input.captured_address ?? null,
        device_captured_at: input.device_captured_at ?? null,
        client_capture_id: clientCaptureId,
        // server_received_at deliberately NOT sent - the server stamps it.
      })
      .select()
      .single();

    if (error) {
      // Already arrived on an earlier attempt - this is success.
      if (isClientCaptureDuplicate(error.code, error.message)) {
        const existing = await fetchExistingCapture(userId, clientCaptureId);
        return { ok: true, entry: existing, alreadyExisted: true };
      }

      console.error('[captureEntryWithResult] insert error:', error.code, error.message);
      return {
        ok: false,
        errorCode: error.code || 'UNKNOWN',
        errorMessage: error.message || 'The server did not accept the capture.',
      };
    }

    return { ok: true, entry: data as TimelineEntry, alreadyExisted: false };
  } catch (err) {
    // Network failures (no signal, server unreachable) usually land here.
    const message = err instanceof Error ? err.message : String(err);
    console.error('[captureEntryWithResult] network or unexpected error:', message);
    return {
      ok: false,
      errorCode: 'NETWORK',
      errorMessage: message || 'Could not reach LukaDuka.',
    };
  }
}
